import React, { useEffect, useRef } from 'react';
import './Modal.css';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// Auto-focus prefers editable form controls; readonly is excluded because
// landing on a read-only "Email" field (UserManagement edit modal) and
// skipping the first editable field is a worse default than the alternative.
const PREFERRED_FOCUS_SELECTOR =
  'input:not([disabled]):not([readonly]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]):not([readonly])';

export function Modal({
  children,
  onClose,
  isOpen = true,
  role,
  'aria-modal': ariaModal,
  'aria-label': ariaLabel,
}) {
  const contentRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  // Hold the latest onClose in a ref so the effect doesn't re-run when callers
  // pass an inline arrow on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Auto-focus: wait one frame so any child autoFocus has fired first.
    let raf;
    if (typeof requestAnimationFrame === 'function') {
      raf = requestAnimationFrame(() => {
        const node = contentRef.current;
        if (!node) return;
        // If something inside the modal already grabbed focus (e.g. <input
        // autoFocus>), don't fight it.
        if (node.contains(document.activeElement) && document.activeElement !== node) {
          return;
        }
        const preferred = node.querySelector(PREFERRED_FOCUS_SELECTOR);
        const fallback = node.querySelector(FOCUSABLE_SELECTOR);
        (preferred || fallback)?.focus();
      });
    }

    const handleKeyDown = (e) => {
      const node = contentRef.current;
      if (!node) return;

      // Only act on Escape when focus is within THIS modal — prevents nested
      // modals (ConfirmDialog inside KPISystemEditModal) from closing both at
      // once on a single Escape press.
      if (e.key === 'Escape' && node.contains(document.activeElement)) {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      if (e.key === 'Tab') {
        const focusables = Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR));
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !node.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !node.contains(active))) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (raf != null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(raf);
      }
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        ref={contentRef}
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        role={role}
        aria-modal={ariaModal}
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </div>
  );
}
